/**
 * Attribution of a prebuilt-wrapper backend error to the form field that caused it.
 *
 * Shared by all three protocols (OAuth/OIDC, Federated SAML, Custom IdP), which call different
 * Core wrapper endpoints but resolve errors the same way.
 */
export interface WrapperFieldErrorRule {
  /** Core ErrorCode this rule applies to. */
  code: number;
  /** Optional exact Description, for codes that cover several causes and only one names a field. */
  description?: string;
  /** The request field the error belongs to, matching the catalogue entry's fieldName. */
  field: string;
}

/**
 * Resolves a wrapper error response to inline field errors, if the field can be identified.
 *
 * Core's error contract already carries attribution — apiError.Errors is
 * []ValidationError{FieldName, ErrorMessage} — so that is preferred wherever Core populates it and
 * the console stops deciding which field an error belongs to. The rule table is only the fallback
 * for the wrapper routes, which today report via ErrorCode/Description alone; as Core fills Errors
 * for those routes the corresponding tables become dead and can be deleted outright.
 *
 * `declaredFields` are the keys the selected catalogue template actually declared, so an error
 * naming a field this template does not render can never attach to a non-existent form item.
 */
export const resolveWrapperFieldError = (response: any, declaredFields: string[], rules: WrapperFieldErrorRule[]): Record<string, string> | null => {
  const fallbackMessage = response?.Description || response?.Message;
  // Without a message there is nothing to render: attributing the error would turn the field red
  // with no text AND suppress the toast, leaving a form that refuses to submit for no stated reason.
  if (!fallbackMessage) {
    return null;
  }

  const reported = Array.isArray(response?.Errors) ? response.Errors : [];
  const attributed = reported
    .filter((error: any) => error?.FieldName && declaredFields.includes(error.FieldName))
    .reduce((acc: Record<string, string>, error: any) => ({ ...acc, [error.FieldName]: error.ErrorMessage || fallbackMessage }), {});
  if (Object.keys(attributed).length) {
    return attributed;
  }

  const match = rules.find((rule) => rule.code === response?.ErrorCode && (rule.description === undefined || rule.description === response?.Description));
  if (!match || !declaredFields.includes(match.field)) {
    return null;
  }
  return { [match.field]: fallbackMessage };
};
